import { gql, useLazyQuery } from '@apollo/client';
import get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';
import React from 'react';
import { useHistory } from 'react-router-dom';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import { OPERATORS_PATH } from '../../Constants';

// Fetch everything about the source operator that isn't already loaded on the Operator edit page.
const COPY_SOURCE_DATA_QUERY = gql`
   query getCopySourceData($operatorId: [Int]) {
      # Zone/segment duplication is temporarily disabled - see the commented-out logic below.
      # zones: zone_AllWhere(zoneSearch: { operatorId: $operatorId }) {
      #    id
      #    name
      # }
      # segments: segment_AllWhere(segmentSearch: { operatorId: $operatorId }) {
      #    id
      #    zoneId
      #    job
      #    serviceLineNumber
      #    lengthFT
      #    pressureTestSegment
      #    diameter
      # }
      testTypes: testType_AllWhere(testTypeSearch: { operatorId: $operatorId }) {
         id
         testType
      }
      testSystemPressures: testSystemPressure_AllWhere(testSystemPressureSearch: { operatorId: $operatorId }) {
         id
         pressureText
      }
      pressureSpecs: pressureSpecs_AllWhere(pressureSpecsSearch: { operatorId: $operatorId }) {
         id
         minimumAllowablePressure
         maximumAllowablePressure
         maximumOnTestPressure
         maximumAllowablePressureDrop
         testType {
            id
         }
         testSystemPressure {
            id
         }
      }
      testParameters: testParameter_AllWhere(testParameterSearch: { operatorId: $operatorId }) {
         id
         minimumPipeDiameter
         minimumPipeLength
         testLength
         testType {
            id
         }
      }
      equalizationTimes: equalizationTime_AllWhere(equalizationTimeSearch: { operatorId: $operatorId }) {
         id
         label
         minutes
      }
   }
`;

// The scalar operator fields copied from the source operator (name, email, and emailRecipient are excluded).
const BASIC_SETTINGS_FIELDS = [
   'defaultStabilizationPeriod', 'autoDeleteDays', 'isActive', 'privacyPolicy', 'sendFailedTests',
   'sendEmail', 'emailType', 'gpsTimeout', 'prohibitLowBatTest', 'testEntireLog',
   'singleSegmentOnly', 'FieldLabel01', 'FieldLabel02', 'FieldLabel03', 'FieldLabel04',
   'FieldLabel05', 'FieldLabel06', 'FieldLabel07', 'FieldLabel08', 'logBeforeTestDuration', 'roundPressureValues',
   'addOriginalPressureToReport', 'certifyText', 'uploadPhotos', 'temperatureCompensation',
];

/**
 * Confirmation dialog that, once accepted, loads the source operator's related data and navigates to the
 * "Create Operator" form pre-filled with a copy of that data. The new operator is only persisted once the
 * user finishes the form and clicks its own Create button.
 */
export default function CopyOperatorDialog({ open, onClose, sourceOperator, sourceOperatorId }) {
   const history = useHistory();

   const [loadSourceData, { loading, error }] = useLazyQuery(COPY_SOURCE_DATA_QUERY,
      { variables: { operatorId: [sourceOperatorId] }, fetchPolicy: 'network-only' });

   const handleClose = () => {
      if (!loading) {
         onClose();
      }
   };

   /**
    * Load the source operator's related data, build the prefill payload, and navigate to the create form.
    */
   const handleSubmit = async (event) => {
      event && event.preventDefault();
      const { data } = await loadSourceData();

      // Zone/segment duplication is temporarily disabled - see the commented-out logic below.
      // const zones = get(data, 'zones') || [];
      // const segments = get(data, 'segments') || [];
      const testTypes = get(data, 'testTypes') || [];
      const testSystemPressures = get(data, 'testSystemPressures') || [];
      const pressureSpecs = get(data, 'pressureSpecs') || [];
      const testParameters = get(data, 'testParameters') || [];
      const equalizationTimes = get(data, 'equalizationTimes') || [];
      const pipeDiameters = get(sourceOperator, 'pipeDiameters') || [];

      // Zone/segment duplication is temporarily disabled.
      // const zoneLocalIdMap = new Map();
      // const draftZones = zones.map(zone => {
      //    const localId = uniqueId('copyZone');
      //    zoneLocalIdMap.set(zone.id, localId);
      //    return { id: localId, name: zone.name };
      // });
      //
      // const draftSegments = segments
      //    .filter(segment => !segment.zoneId || zoneLocalIdMap.has(segment.zoneId))
      //    .map(segment => ({
      //       id: uniqueId('copySegment'),
      //       zoneId: segment.zoneId ? zoneLocalIdMap.get(segment.zoneId) : undefined,
      //       job: segment.job,
      //       serviceLineNumber: segment.serviceLineNumber,
      //       lengthFT: segment.lengthFT,
      //       pressureTestSegment: segment.pressureTestSegment,
      //       diameter: segment.diameter,
      //    }));

      const testTypeLocalIdMap = new Map();
      const testTypeById = new Map();
      const draftTestTypes = testTypes.map(testType => {
         const localId = uniqueId('copyTestType');
         testTypeLocalIdMap.set(testType.id, localId);
         const draftTestType = { id: localId, testType: testType.testType };
         testTypeById.set(localId, draftTestType);
         return draftTestType;
      });

      const testSystemPressureLocalIdMap = new Map();
      const testSystemPressureById = new Map();
      const draftTestSystemPressures = testSystemPressures.map(testSystemPressure => {
         const localId = uniqueId('copyTestSystemPressure');
         testSystemPressureLocalIdMap.set(testSystemPressure.id, localId);
         const draftTestSystemPressure = { id: localId, pressureText: testSystemPressure.pressureText };
         testSystemPressureById.set(localId, draftTestSystemPressure);
         return draftTestSystemPressure;
      });

      const draftPressureSpecs = pressureSpecs
         .filter(spec => testTypeLocalIdMap.has(get(spec, 'testType.id')) && testSystemPressureLocalIdMap.has(get(spec, 'testSystemPressure.id')))
         .map(spec => ({
            id: uniqueId('copyPressureSpec'),
            minimumAllowablePressure: spec.minimumAllowablePressure,
            maximumAllowablePressure: spec.maximumAllowablePressure,
            maximumOnTestPressure: spec.maximumOnTestPressure,
            maximumAllowablePressureDrop: spec.maximumAllowablePressureDrop,
            testType: testTypeById.get(testTypeLocalIdMap.get(spec.testType.id)),
            testSystemPressure: testSystemPressureById.get(testSystemPressureLocalIdMap.get(spec.testSystemPressure.id)),
         }));

      const draftTestParameters = testParameters
         .filter(testParameter => testTypeLocalIdMap.has(get(testParameter, 'testType.id')))
         .map(testParameter => ({
            id: uniqueId('copyTestParameter'),
            minimumPipeDiameter: testParameter.minimumPipeDiameter,
            minimumPipeLength: testParameter.minimumPipeLength,
            testLength: testParameter.testLength,
            testType: testTypeById.get(testTypeLocalIdMap.get(testParameter.testType.id)),
         }));

      const draftPipeDiameters = pipeDiameters.map(pipeDiameter => ({
         id: uniqueId('copyPipeDiameter'),
         diameter: pipeDiameter.diameter,
      }));

      const draftEqualizationTimes = equalizationTimes.map(equalizationTime => ({
         id: uniqueId('copyEqualizationTime'),
         label: equalizationTime.label,
         minutes: equalizationTime.minutes,
      }));

      const basicVariables = BASIC_SETTINGS_FIELDS.reduce((accumulator, field) => {
         accumulator[field] = get(sourceOperator, field);
         return accumulator;
      }, {});

      const hasCalibrationSettings = !!get(sourceOperator, 'calibrationSettings.id');

      const initialOperator = {
         ...basicVariables,
         name: `${get(sourceOperator, 'name')} - copy`,
         ...(hasCalibrationSettings ? {
            calibrationSettings: {
               validityPeriod: get(sourceOperator, 'calibrationSettings.validityPeriod'),
               warningPeriod: get(sourceOperator, 'calibrationSettings.warningPeriod'),
            },
         } : {}),
         multipleSegmentPipe: draftPipeDiameters.length > 0,
         equalizationTimeCalculation: draftEqualizationTimes.length > 0,
         // Zone/segment duplication is temporarily disabled.
         // zones: draftZones,
         // segments: draftSegments,
         testTypes: draftTestTypes,
         testSystemPressures: draftTestSystemPressures,
         pressureSpecs: draftPressureSpecs,
         testParameters: draftTestParameters,
         pipeDiameters: draftPipeDiameters,
         equalizationTimes: draftEqualizationTimes,
      };

      onClose();
      history.push(OPERATORS_PATH, { isCreate: true, initialOperator });
   };

   return (
      <ModalDialog
         open={open}
         titleKey={'operator.duplicate.title'}
         titleValues={{ name: get(sourceOperator, 'name') }}
         messageKey={'operator.duplicate.message'}
         messageValues={{ name: get(sourceOperator, 'name') }}
         submitKey={'operator.duplicate.submit.button'}
         onSubmit={handleSubmit}
         onClose={handleClose}
         isSaving={loading}
         isEnabled={!loading}
         maxWidth={'sm'}
         fullWidth
      >
         <DataLoadingAndErrors error={error} isLoading={loading} />
      </ModalDialog>
   );
}
